import { AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { AppUtils } from '../../../core/config/app.utils';
import { LoadingService } from '../../../core/services/loading.service';
import { FileService } from '../../../core/services/file.service';
import { SettingService } from '../../../core/services/setting.service';
import { ActionAlert } from '../../../core/dto/actionAlert';
import { AuthService } from '../../../modules/auth/services/auth.service';
import { LanguageService } from '../../../core/services/language.service';
import { CountryService } from '../../../core/services/country.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { DefaultPageSize } from '../../../core/dto/defaultPage.size';
import { Pagination } from '../../../core/dto/pagination';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { CategoryService } from '../../../core/services/category.service';
import { AddEditRemarksTitleComponent } from '../../itineraries/add-edit-remarks-title/add-edit-remarks-title.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AddEditCategoriesComponent } from '../../../modules/shared/add-edit-categories/add-edit-categories.component';
import { environment } from '../../../../environments/environment';
import { driver, Driver } from 'driver.js';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-branding',
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss'],
})
export class BrandingComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly environment = environment;
  companyForm: any;
  formErrors: any;
  files: File[] = [];
  brand: any;
  brandId: any;
  actionMessage = new ActionAlert();
  user: any;
  uploadAndPreview = true;
  msg: any;
  placeholderPath: any = '../assets/img/placeholder-image.png';
  loggedUser: any;
  selectedLanguages: any = [];
  translationLanguages: any;
  selectedDefaultLanguage: any;
  countries: any[] = [];

  brandColorTheme1 = ``;
  brandColorTheme2 = ``;
  driverObj!: Driver;
  isTour: boolean = false;
  constructor(
    private fb: FormBuilder,
    private loading: LoadingService,
    private fileService: FileService,
    private settingService: SettingService,
    private authService: AuthService,
    private languageService: LanguageService,
    private loadingService: LoadingService,
    private countryService: CountryService,
    private route: ActivatedRoute,
    private translocoService: TranslocoService
  ) {
    this.formErrors = {
      companyName: {},
      country: {},
      email: {},
      logo: {},
      websiteLink: {},
      summary: {},
      address: {}
    };
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.isTour = params['isTour'] === 'true';
    });
    this.loggedUser = this.authService.getLoggedInUser();
    this.getCountries().then();
    this.getLanguages().then();
    this.findBrand().then();

    this.companyForm = this.fb.group({
      companyName: ['', [Validators.required]],
      country: ['', [Validators.required]],
      address: [''],
      logo: ['', [Validators.required]],
      websiteLink: [
        '',
        [Validators.required, Validators.pattern(SETTINGS.REGEX.URL.EXP)],
      ],
      summary: ['', [Validators.required]],
    });

    this.companyForm.valueChanges.subscribe(() => {
      AppUtils.getFormErrors(this.companyForm, this.formErrors);
    });
  }

  ngAfterViewInit(): void {
    if (this.isTour) {
      setTimeout(() => {
        this.startAccountInfoTour();
      }, 300);
    }
  }

  ngOnDestroy(): void {
    if (this.driverObj) {
      this.driverObj.destroy();
    }
  }

  async findBrand(): Promise<any> {
    this.brand = await this.settingService.findBrand(this.loggedUser.userId);
    this.brandId = this.brand._id;
    const { companyDetails } = this.brand;
    this.companyForm.patchValue({
      ...this.brand,
      summary: companyDetails.summary,
      websiteLink: companyDetails.websiteLink,
    });
    this.companyForm.get('country').setValue(this.brand?.country?.code);
    this.brandColorTheme1 = this.brand.uiOptions[0].brandColor_heading;
    this.brandColorTheme2 = this.brand.uiOptions[1].brandColor_heading;
  }

  onSelect(event: any): void {
    this.loading.start();
    this.files.push(...event.addedFiles);
    this.fileService.postFile(this.files[0]).subscribe(res => {
      this.companyForm.get('logo').setValue(res.url);
      this.uploadAndPreview = true;
      this.loading.stop();
    });
  }

  onRemove(event: any): void {
    this.files.splice(this.files.indexOf(event), 1);
    this.companyForm.get('logo').setValue('');
  }

  async createOrUpdateBrand(type: any): Promise<any> {
    this.loading.start();
    if (this.brandId === undefined) {
      try {
        const payload = {
          ...this.companyForm.getRawValue(),
          companyDetails: {},
        };
        const selected_country = this.countries.find(
          c => c.code === payload.country
        );
        payload.country = {
          name: selected_country.name,
          code: selected_country.code,
          emoji: selected_country.emoji,
        };
        // summary is company description
        payload.companyDetails = {
          summary: payload.summary,
          websiteLink: payload.websiteLink,
        };
        await this.settingService.createBrand(payload);
        await this.findBrand();
        this.msg = type;
        this.actionMessage = new ActionAlert(
          true,
          true,
          this.translocoService.translate('settings.branding.messages.companyInfoCreated'),
          'success'
        );
        this.loading.stop();
      } catch (e) {
        this.msg = type;
        this.actionMessage = new ActionAlert(
          true,
          true,
          this.translocoService.translate('settings.branding.messages.companyInfoNotCreated'),
          'error'
        );
        this.loading.stop();
      }
    } else {
      try {
        const payload = {
          _id: this.brandId,
          ...this.companyForm.getRawValue(),
          companyDetails: {},
        };
        const selected_country = this.countries.find(
          c => c.code === payload.country
        );
        payload.country = {
          name: selected_country.name,
          code: selected_country.code,
          emoji: selected_country.emoji,
        };
        // summary is company description
        payload.companyDetails = {
          summary: payload.summary,
          websiteLink: payload.websiteLink,
        };
        await this.settingService.updateBrand(payload);
        this.msg = type;
        this.actionMessage = new ActionAlert(
          true,
          true,
          this.translocoService.translate('settings.branding.messages.companyInfoUpdated'),
          'success'
        );
        this.loading.stop();
      } catch (e) {
        this.msg = type;
        this.actionMessage = new ActionAlert(
          true,
          true,
          this.translocoService.translate('settings.branding.messages.companyInfoNotUpdated'),
          'error'
        );
        this.loading.stop();
      }
    }
  }

  async updateBrandUIOptions(messageType: string): Promise<void> {
    this.loading.start();
    const payload = {
      _id: this.brandId,
      uiOptions: [
        {
          brandColor_heading: this.brandColorTheme1,
        },
        {
          brandColor_heading: this.brandColorTheme2,
        },
      ],
    };
    try {
      await this.settingService.updateBrand(payload);
      this.msg = messageType;
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate('settings.branding.messages.brandColorsUpdated'),
        'success'
      );
      this.loading.stop();
    } catch (e) {
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate('settings.branding.messages.brandColorsNotUpdated'),
        'error'
      );
      this.loading.stop();
    }
  }

  isValidHexColor(color: string): boolean {
    return /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test((color || '').trim());
  }

  async getLanguages(): Promise<any> {
    this.translationLanguages =
      await this.languageService.getContentLanguages();
  }

  async getCountries(): Promise<void> {
    this.countries = await this.countryService.getCountries();
  }
  orderByName(list: any[]): any {
    if (list && list.length > 0) {
      return AppUtils.sortOrderByName(list);
    }
  }

  startAccountInfoTour(): void {
     this.driverObj = driver({
       animate: true,
       smoothScroll: true,
       allowClose: true,
       overlayOpacity: 0.35,
       stagePadding: 8,
       stageRadius: 10,
       showProgress: false,
       popoverClass: 'company-info-driver-theme',
       steps: [
         {
           element: '#company-info-tour',
           popover: {
             title: this.translocoService.translate('settings.branding.tour.title'),
             description:
               this.translocoService.translate('settings.branding.tour.description'),
             side: 'right',
             align: 'start',
             showButtons: ['next'],
             nextBtnText: this.translocoService.translate('settings.branding.tour.done')
           }
         }
       ]
     });

     this.driverObj.drive();
   }
}
